<script>
import ImageUploader from '@lib/components/image-uploader'
import { mask } from 'vue-the-mask'
import { datePickerFormat } from '@lib/utils/date-helper'
import get from 'lodash/get'

export default {
  name: 'ProductFormProductStep',
  directives: {
    mask,
  },
  components: {
    ImageUploader,
  },
  props: {
    form: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      required: true,
      validator: v => !v || /supplier|provider/.test(v),
    },
    tree: {
      type: Object,
      default: () => {},
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      datePickerFormat,
      rules: {
        'name': [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'images': [
          {
            required: true,
            message: 'At least one image is required',
            trigger: 'blur',
          },
        ],
        'description': [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'brand': [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'sku': [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
      },
      selectedGroup: null,
      selectedTier: null,
      selectedCategory: null,
      loaded: false,
    }
  },
  computed: {
    depth() {
      return this.getDepth(this.tree)
    },
    groups() {
      return this.depth === 3 ? this.tree.categories : null
    },
    selectedGroupId() {
      return this.selectedGroup && this.selectedGroup.id
    },
    tiers() {
      switch (this.depth) {
        case 3:
          return this.selectedGroup ? this.selectedGroup.items : []
        case 2:
          return this.tree.categories
        default:
          return null
      }
    },
    selectedTierId() {
      return this.selectedTier && this.selectedTier.id
    },
    categories() {
      switch (this.depth) {
        case 2:
          return this.selectedTier ? this.selectedTier.items : []
        case 3:
          return this.selectedTier ? this.selectedTier.items : []
        case 1:
          return this.tree.categories
        default:
          return null
      }
    },
    selectedCategoryId() {
      return this.selectedCategory && this.selectedCategory.id
    },
  },
  watch: {
    selectedGroup(val) {
      if (!this.loaded) return
      this.selectedTier = null
    },
    selectedTier(val) {
      if (!this.loaded) return
      this.selectedCategory = null
    },
    selectedCategory(val) {
      this.changeValue('categories', [this.selectedCategoryId])
    },
  },
  mounted() {
    if (this.type === 'provider' && this.form.categories.length) {
      const categoryId = get(this.form.categories.find(cat => cat.parent_id), 'id') || this.form.categories[0]
      this.selectedCategory = this.findBranch(this.tree, categoryId)
      if (this.depth > 1) {
        this.selectedTier = this.findBranch(this.tree, this.selectedCategory.parent_id)
      }
      if (this.depth > 2) {
        this.selectedGroup = this.findBranch(this.tree, this.selectedTier.parent_id)
      }
    }
    this.$nextTick(() => {
      this.loaded = true
    })
  },
  methods: {
    changeValue(field, value, index) {
      this.$emit('change', { field, value, index })
    },
    getDepth(item, depth = 0) {
      const items = item.categories || item.items
      if (items.length) {
        return this.getDepth(items[0], depth + 1)
      }
      return depth
    },
    findBranch(item, id) {
      if (item.id === id) {
        return item
      } else {
        const items = item.categories || item.items
        for (let i = 0; i < items.length; i++) {
          const result = this.findBranch(items[i], id)
          if (result) {
            return result
          }
        }
      }
    },
    updateCategory(type, id) {
      switch (type) {
        case 'category':
          this.selectedCategory = this.categories.find(item => item.id === id)
          break
        case 'tier':
          this.selectedTier = this.tiers.find(item => item.id === id)
          break
        case 'group':
          this.selectedGroup = this.groups.find(item => item.id === id)
          break
      }
    },
  },
}
</script>

<template>
  <div :class="$style.wrapper">
    <el-form
      class="modal-form"
      label-position="top"
      :model="form"
      :rules="rules"
    >
      <div class="united-field">
        <el-form-item
          label="Product Name"
          prop="name"
        >
          <el-input
            :value="form.name"
            @input="changeValue('name', $event)"
          />
        </el-form-item>
        <el-form-item
          label="Availability Status"
          prop="availability.status"
        >
          <el-select
            :value="form.availability.status"
            @input="changeValue('availability.status', $event)"
          >
            <el-option
              label="None"
              :value="null"
            />
            <el-option
              label="Available"
              value="Available"
            />
            <el-option
              label="Preorder"
              value="Preorder"
            />
            <el-option
              label="Discontinued"
              value="Discontinued"
            />
          </el-select>
        </el-form-item>
      </div>

      <el-form-item
        label="Description"
        prop="description"
      >
        <el-input
          type="textarea"
          rows="6"
          :value="form.description"
          @input="changeValue('description', $event)"
        />
      </el-form-item>

      <div class="united-field">
        <div class="united-field">
          <el-form-item
            label="Availability Start Date"
          >
            <el-date-picker
              v-mask="['##/##/####']"
              :value="form.availability.start_date"
              :value-format="datePickerFormat"
              type="date"
              format="dd/MM/yyyy"
              placeholder="Enter Date"
              @input="changeValue('availability.start_date', $event)"
            />
          </el-form-item>

          <el-form-item
            label="Availability End Date"
          >
            <el-date-picker
              v-mask="['##/##/####']"
              :value="form.availability.end_date"
              :value-format="datePickerFormat"
              type="date"
              format="dd/MM/yyyy"
              placeholder="Enter Date"
              @input="changeValue('availability.end_date', $event)"
            />
          </el-form-item>
        </div>
      </div>

      <div class="united-field">
        <el-form-item
          label="Allocated Stock Quantity"
          prop="allocated_stock_qty"
        >
          <el-input
            :value="form.allocated_stock_qty"
            type="number"
            min="0"
            @input="changeValue('allocated_stock_qty', $event)"
          />
        </el-form-item>

        <el-form-item
          v-if="type === 'supplier' && isEdit"
          label="Lifetime Sales"
          prop="lifetime_sales"
        >
          <el-input
            :value="form.lifetime_sales"
            :disabled="type === 'supplier' && isEdit"
            @input="changeValue('lifetime_sales', $event)"
          />
        </el-form-item>
      </div>

      <el-form-item
        label="Image URL/s"
        prop="images"
      >
        <div
          v-if="form.images.length"
          :class="$style.urls"
        >
          <div
            :class="$style.addUrl"
            @click="changeValue('images', '', 'new')"
          >
            <el-icon name="plus" />
            Add Url
          </div>
          <image-uploader
            v-for="(image, index) in form.images"
            :key="index"
            :image="image"
            :class="$style.uploader"
            @onUpload="changeValue('images', $event.cdnUrl, index)"
          >
            <span slot="button-text">
              Upload
            </span>
          </image-uploader>
        </div>
        <image-uploader
          v-else
          @onUpload="changeValue('images', $event.cdnUrl)"
        >
          <span slot="button-text">
            Upload
          </span>
        </image-uploader>
      </el-form-item>
      <el-form-item
        label="Brand"
        prop="brand"
      >
        <el-input
          :value="form.brand"
          @input="changeValue('brand', $event)"
        />
      </el-form-item>

      <div class="united-field">
        <el-form-item
          label="SKU"
          prop="sku"
        >
          <el-input
            :value="form.sku"
            @input="changeValue('sku', $event)"
          />
        </el-form-item>
        <el-form-item
          label="GTIN"
        >
          <el-input
            :value="form.gtin"
            @input="changeValue('gtin', $event)"
          />
        </el-form-item>
      </div>

      <div class="united-field">
        <el-form-item
          label="Model"
        >
          <el-input
            :value="form.model"
            @input="changeValue('model', $event)"
          />
        </el-form-item>
        <el-form-item
          label="MPN"
        >
          <el-input
            :value="form.mpn"
            @input="changeValue('mpn', $event)"
          />
        </el-form-item>
      </div>

      <span :class="$style.subtitle">Categories</span>

      <template v-if="type === 'provider'">
        <div :class="$style.categoryTree">
          <el-form-item label="Product Category Tree">
            <el-select
              v-model="tree.name"
              disabled
            />
          </el-form-item>
        </div>
        <div
          v-if="tree.name"
          :class="$style.categories"
        >
          <el-form-item
            v-if="groups"
            label="Product Group"
          >
            <el-select
              :value="selectedGroupId"
              @input="updateCategory('group', $event)"
            >
              <el-option
                v-for="group in groups"
                :key="group.id"
                :label="group.name"
                :value="group.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item
            label="Product Tiers"
          >
            <el-select
              :value="selectedTierId"
              @input="updateCategory('tier', $event)"
            >
              <el-option
                v-for="tier in tiers"
                :key="tier.id"
                :label="tier.name"
                :value="tier.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item
            label="Product Categories"
          >
            <el-select
              :value="selectedCategoryId"
              @input="updateCategory('category', $event)"
            >
              <el-option
                v-for="category in categories"
                :key="category.id"
                :label="category.name"
                :value="category.id"
              />
            </el-select>
          </el-form-item>
        </div>
      </template>
      <template v-else-if="type==='supplier'">
        <div :class="$style.category">
          <el-form-item>
            <el-input v-model="form.categories" />
          </el-form-item>
        </div>
      </template>
    </el-form>
  </div>
</template>

<style lang="scss" module>
.labelLeft {
  display: flex;
  align-items: center;
  :global {
    .el-form-item__label {
      margin-right: 3rem;
    }
  }
}

.subtitle {
  display: block;
  margin: 2rem 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.categoryTree, {
  width: calc((100% - 2 * #{rem(30px)}) / 3);
}

.categories {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: rem(30px);
}

.category {
  width: calc(50% - #{rem(15px)});
}

.urls {
  display: flex;
  flex-direction: column;
  width: 100%;

  .uploader {
    margin-bottom: 1rem;
  }
}

.addUrl {
  position: absolute;
  top: -2.1rem;
  right: 0;
  display: block;
  width: 4rem;
  color: var(--color-primary);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
